const SHARED_FILTER_FLAG = 'feed';
const SHARED_FILTER_VALUE = '1';
const MAX_ITEMS = 150;
const MAX_NAME_LENGTH = 80;
const MAX_QUERY_LENGTH = 500;
const MAX_VALUE_LENGTH = 160;
const SHARED_FILTER_KEYS = [
  SHARED_FILTER_FLAG,
  'name',
  'q', 'keywords', 'keywordMode', 'searchAll',
  'tagMode',
  'portalMode',
  'from',
  'to',
  'category',
  'subcategory',
  'portal',
  'country'
];

const cleanText = (value, maxLength = MAX_VALUE_LENGTH) => (
  String(value || '').trim().slice(0, maxLength)
);

const uniqueValues = (values) => Array.from(new Set(values.filter(Boolean))).slice(0, MAX_ITEMS);

const validDate = (value) => (
  /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(value) ? value : ''
);

const appendMany = (params, key, values) => {
  uniqueValues(values).forEach(value => params.append(key, value));
};

export const hasSharedFilter = (search = '') => {
  try {
    return new URLSearchParams(search).get(SHARED_FILTER_FLAG) === SHARED_FILTER_VALUE;
  } catch {
    return false;
  }
};

export const createShareableFilterUrl = (filter, baseHref) => {
  const url = new URL(baseHref);
  const params = new URLSearchParams();
  const selectedTags = Array.isArray(filter?.selected_tags) ? filter.selected_tags : [];
  const categoryNames = uniqueValues(selectedTags
    .filter(tag => tag?.type === 'category')
    .map(tag => cleanText(tag.name)));
  const categorySet = new Set(categoryNames);
  const subcategories = selectedTags
    .filter(tag => tag?.type === 'subcategory' && !categorySet.has(cleanText(tag.parent)))
    .map(tag => JSON.stringify([
      cleanText(tag.parent),
      cleanText(tag.name)
    ]));

  params.set(SHARED_FILTER_FLAG, SHARED_FILTER_VALUE);

  const query = cleanText(filter?.query, MAX_QUERY_LENGTH);
  const fromDate = validDate(cleanText(filter?.from_date));
  const toDate = validDate(cleanText(filter?.to_date));

  if (query) params.set('q', query);
  if (filter?.multi_keyword !== false) params.set('keywords', '1');
  else params.set('keywords', '0');
  if (filter?.keyword_mode === 'all') params.set('keywordMode', 'all');
  if (filter?.search_all) params.set('searchAll', '1');
  if (filter?.include_tags === false) params.set('tagMode', 'exclude');
  if (filter?.include_portals === false) params.set('portalMode', 'exclude');
  if (fromDate) params.set('from', fromDate);
  if (toDate) params.set('to', toDate);

  appendMany(params, 'category', categoryNames);
  appendMany(params, 'subcategory', subcategories);
  appendMany(params, 'portal', (filter?.selected_portals || []).map(value => cleanText(value)));
  appendMany(params, 'country', (filter?.selected_countries || []).map(value => cleanText(value)));

  url.search = params.toString();
  url.hash = '';
  return url.toString();
};

export const removeSharedFilterFromUrl = (baseHref) => {
  const url = new URL(baseHref);
  SHARED_FILTER_KEYS.forEach(key => url.searchParams.delete(key));
  return url.toString();
};

export const readSharedFilter = (search, categories = {}) => {
  if (!hasSharedFilter(search)) return null;

  const params = new URLSearchParams(search);
  const selectedTags = [];
  const seenTags = new Set();
  const addTag = (tag) => {
    const key = `${tag.type}:${tag.parent || ''}:${tag.name}`;
    if (!tag.name || seenTags.has(key) || seenTags.size >= MAX_ITEMS) return;
    seenTags.add(key);
    selectedTags.push(tag);
  };

  uniqueValues(params.getAll('category').map(value => cleanText(value))).forEach(categoryName => {
    const subcategories = Array.isArray(categories?.[categoryName])
      ? categories[categoryName]
      : [];

    if (subcategories.length === 0) return;
    addTag({ type: 'category', name: categoryName });
    subcategories.forEach(name => addTag({
      type: 'subcategory',
      name: cleanText(name),
      parent: categoryName
    }));
  });

  params.getAll('subcategory').slice(0, MAX_ITEMS).forEach(value => {
    try {
      const [parent, name] = JSON.parse(value);
      addTag({
        type: 'subcategory',
        name: cleanText(name),
        parent: cleanText(parent) || null
      });
    } catch {
      // Ignore malformed shared values while preserving the rest of the feed.
    }
  });

  return {
    name: cleanText(params.get('name'), MAX_NAME_LENGTH) || null,
    query: cleanText(params.get('q'), MAX_QUERY_LENGTH),
    multi_keyword: params.get('keywords') !== '0',
    keyword_mode: params.get('keywordMode') === 'all' ? 'all' : 'any',
    search_all: params.get('searchAll') === '1',
    include_tags: params.get('tagMode') !== 'exclude',
    selected_tags: selectedTags,
    from_date: validDate(cleanText(params.get('from'))),
    to_date: validDate(cleanText(params.get('to'))),
    include_portals: params.get('portalMode') !== 'exclude',
    selected_portals: uniqueValues(params.getAll('portal').map(value => cleanText(value))),
    selected_countries: uniqueValues(params.getAll('country').map(value => cleanText(value)))
  };
};
